// src/Studio/canvas/utils/apply3DToNode.ts
// Utility to compute pseudo-3D transforms for Konva nodes
// Since Konva is 2D canvas, we simulate 3D using skew, scale, and shadow

export interface Transform3DValues {
  rotateX: number;      // Tilt forward/back: -45 to 45
  rotateY: number;      // Tilt left/right: -45 to 45
  rotateZ: number;      // Z rotation: -180 to 180
  perspective: number;  // 200 to 2000 (affects transform intensity)
  // Stored as 0..1 internally. UI slider shows 0..100% and divides by 100 before storing.
  // normalizeDepthShadow() handles legacy values >1 by dividing by 100 for backward compat.
  depthShadow: number;
}

export const DEFAULT_3D_VALUES: Transform3DValues = {
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
  perspective: 1000,
  depthShadow: 0,
};

function coerceNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    // Handles "-20", "600px", "30%", "-20deg" etc.
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function normalizeDepthShadow(v: unknown): number {
  const raw = coerceNumber(v, 0);
  // Backward compatible:
  // - old code stored 0..1
  // - UI often stores 0..100
  const as01 = raw > 1 ? raw / 100 : raw;
  return Math.max(0, Math.min(1, as01));
}

/**
 * Check if element has any 3D transforms applied
 */
export function has3DTransformValues(values: Partial<Transform3DValues>): boolean {
  const rx = coerceNumber(values.rotateX, 0);
  const ry = coerceNumber(values.rotateY, 0);
  const rz = coerceNumber(values.rotateZ, 0);
  const p = coerceNumber(values.perspective, 1000);
  const ds = normalizeDepthShadow(values.depthShadow);

  return rx !== 0 || ry !== 0 || rz !== 0 || p !== 1000 || ds !== 0;
}

// --- Tuning constants for the 2D→3D approximation ---
// SKEW_HALF_ANGLE: We use half the rotation angle (0.5×) so tan() stays in a
//   well-behaved range even at ±45°.  Full angle would give extreme skew.
// SKEW_MAGNITUDE (30): Controls how many degrees of skew are produced per unit
//   of tan(halfAngle).  Higher = more dramatic perspective effect.
// FORESHORTEN (0.15): Maximum scale reduction at 90° tilt (15%).
//   Simulates the way tilted surfaces appear narrower.
const SKEW_HALF_ANGLE = 0.5;
const SKEW_MAGNITUDE = 30;
const FORESHORTEN = 0.15;

/**
 * Convert 3D rotation values to 2D skew/scale approximation.
 * Since Konva is a 2D canvas, true 3D projection isn't available.
 * We approximate it: Y-rotation → X-skew (horizontal shear) and vice-versa,
 * with scale reduction to simulate foreshortening.
 */
function compute3DToSkewScale(
  rotateX: number,
  rotateY: number,
  perspective: number
): { skewX: number; skewY: number; scaleX: number; scaleY: number } {
  // perspectiveFactor: 0.2 (extreme, perspective=200) → 2.0 (subtle, perspective=2000)
  const perspectiveFactor = Math.max(200, Math.min(2000, perspective)) / 1000;

  const radX = (rotateX * Math.PI) / 180;
  const radY = (rotateY * Math.PI) / 180;

  // Skew intensity inversely proportional to perspective distance
  const skewIntensity = 1 / perspectiveFactor;
  const skewX = Math.tan(radY * SKEW_HALF_ANGLE) * skewIntensity * SKEW_MAGNITUDE;
  const skewY = Math.tan(radX * SKEW_HALF_ANGLE) * skewIntensity * SKEW_MAGNITUDE;

  // Foreshortening: scale shrinks as surface tilts away from viewer
  const scaleX = 1 - Math.abs(Math.sin(radY)) * FORESHORTEN * skewIntensity;
  const scaleY = 1 - Math.abs(Math.sin(radX)) * FORESHORTEN * skewIntensity;

  return {
    skewX: Math.max(-45, Math.min(45, skewX)),
    skewY: Math.max(-45, Math.min(45, skewY)),
    scaleX: Math.max(0.5, Math.min(1.5, scaleX)),
    scaleY: Math.max(0.5, Math.min(1.5, scaleY)),
  };
}

/**
 * Calculate shadow properties for depth effect
 * At 100% depthShadow, the shadow should cover roughly 50% of element size
 */
function computeDepthShadow(
  rotateX: number,
  rotateY: number,
  depthShadow: number
): { shadowOffsetX: number; shadowOffsetY: number; shadowBlur: number; shadowOpacity: number } {
  if (depthShadow <= 0) {
    return { shadowOffsetX: 0, shadowOffsetY: 0, shadowBlur: 0, shadowOpacity: 0 };
  }

  // Shadow direction based on rotation - increased for larger coverage
  // At 100%, offset can reach ~50px which creates significant depth appearance
  const shadowX = Math.sin((rotateY * Math.PI) / 180) * 50 * depthShadow;
  const shadowY = -Math.sin((rotateX * Math.PI) / 180) * 50 * depthShadow + 25 * depthShadow;

  // Blur scales from 15 to 80 for large soft shadow coverage
  const blur = 15 + 65 * depthShadow;

  // Opacity scales from 0.15 to 0.5 for visible but not overwhelming shadow
  const opacity = 0.15 + 0.35 * depthShadow;

  return {
    shadowOffsetX: shadowX,
    shadowOffsetY: shadowY,
    shadowBlur: blur,
    shadowOpacity: opacity,
  };
}

/**
 * Get computed Konva attributes for 3D transform
 * Used when rendering elements to apply 3D effects
 */
export function get3DNodeAttrs(
  values: Partial<Transform3DValues>,
  baseScaleX: number = 1,
  baseScaleY: number = 1,
  baseRotation: number = 0
): {
  skewX: number;
  skewY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowOpacity: number;
  shadowEnabled: boolean;
} {
  const rotateX = coerceNumber(values.rotateX, 0);
  const rotateY = coerceNumber(values.rotateY, 0);
  const rotateZ = coerceNumber(values.rotateZ, 0);
  const perspective = coerceNumber(values.perspective, 1000);
  const depthShadow = normalizeDepthShadow(values.depthShadow);

  const skewScale = compute3DToSkewScale(rotateX, rotateY, perspective);
  const shadow = computeDepthShadow(rotateX, rotateY, depthShadow);

  return {
    skewX: skewScale.skewX,
    skewY: skewScale.skewY,
    scaleX: baseScaleX * skewScale.scaleX,
    scaleY: baseScaleY * skewScale.scaleY,
    // Add rotateZ to the base rotation
    rotation: baseRotation + rotateZ,
    shadowColor: 'rgba(0, 0, 0, 1)',
    shadowBlur: shadow.shadowBlur,
    shadowOffsetX: shadow.shadowOffsetX,
    shadowOffsetY: shadow.shadowOffsetY,
    shadowOpacity: shadow.shadowOpacity,
    shadowEnabled: depthShadow > 0,
  };
}
